<template>
  <ShipmentMap
    v-if="shipment?.id"
    :id="mapId"
    :shipment="shipment"
    :events="shipmentEvents"
    :static-map="staticMapUrl.data"
    :access-token="accessToken"
    :loading="isAnyLoading || staticMapUrl.loading"
    :dynamic="showDynamicMap"
    :interactive="isMapActionsEnabled"
  />
  <Content
    id="carrier-portal"
    class="carrier-portal"
    :header-dark-bg="!!shipment?.id"
    :header-label="t('shipments.detailsForShipmentId', { shipmentId })"
    :loading="isAnyLoading"
    header-icon="map-marked-alt"
    remove-card-padding
    remove-aside-card-padding
  >
    <template #headerRight>
      <CarrierPortalShipmentActionButton
        v-model:map-expanded="isMapExpanded"
        :shipment-summary="shipmentSummary"
        :service-incident-types="serviceIncidentTypes.data"
        :loading="isAnyLoading"
        @command="onCommand"
      />
    </template>

    <template #aside>
      <CarrierPortalDriverContactSection
        id="driver-contact"
        :shipment="shipment"
        :editable="isAccepted"
      />

      <ReferenceNumbersSection
        id="reference-numbers"
        v-model="allReferenceNumbers"
        :resource-id="shipmentId"
        :editable="false"
      />

      <CarrierPortalAccessorialSection
        v-if="shipment?.requestedAccessorials?.length"
        id="accessorials"
        :accessorials="shipment.requestedAccessorials"
      />

      <BillToSection
        id="bill-to"
        :bill-to="shipment?.billTo"
        :editable="false"
      />

      <CarrierPortalContactSection
        v-if="shipmentSummary?.shipmentEmailContact"
        id="contact"
        :contact="shipmentSummary?.shipmentEmailContact"
      />

      <CarrierPortalNotesSection
        id="notes"
        :carrier-notes="shipment?.carrierNotes"
      />

      <CarrierPortalDocumentSection
        v-if="shipment?.id && isAccepted"
        id="documents"
        ref="documentSectionRef"
        :shipment-id="shipment.id"
        :loading="isPrinting"
        :additional-required-document-types="
          carrierSetting.data?.requiredDocumentTypes ?? []
        "
        :required-document-types-loading="carrierSetting.loading"
        :sent-from-required-documents-email="sentFromRequiredDocumentsEmail"
      />

      <CarrierPortalChargesSection
        id="charges"
        :charges="charges"
        :currency-code="acceptedRateQuote?.currencyCode"
      />
    </template>

    <CarrierPortalHighlightSection
      id="highlights"
      :shipment="shipment"
      :accepted-rate-quote="acceptedRateQuote"
    />

    <section
      v-if="!(loading || isProcessingResponse) && shipmentSummary"
      class="detail-section"
    >
      <MessageContainer :messages="messages" message-class="rygen-message" />
    </section>

    <CarrierPortalLocationSection id="locations" :shipment="shipment" />

    <CarrierPortalTimelineSection
      id="events"
      ref="timelineSectionRef"
      :shipment-events="shipmentEvents"
      :shipment-service-incidents="serviceIncidents"
      :shipment="shipment"
      :shipment-stops="shipmentStops"
      :service-incident-types="serviceIncidentTypes"
      :tender-requests="
        shipmentSummary?.tenderRequest ? [shipmentSummary.tenderRequest] : []
      "
      @save="loadShipmentSummary"
    />

    <CarrierPortalLineItemSection
      id="line-items"
      :line-items="shipment?.lineItems"
    />

    <CarrierPortalUpdateExpectedArrivalModal
      v-model:visible="updateExpectedArrivalModal.visible"
      :related-stop="updateExpectedArrivalModal.relatedStop"
      @save="onUpdateExpectedArrival"
    />
  </Content>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { DateTime } from 'luxon';
import { usePrimeVue } from 'primevue/config';
import { useConfirm } from 'primevue/useconfirm';
import type { PropType, Ref } from 'vue';
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  toRef,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useScrollAnimations, useToast } from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  CarrierSetting,
  Loadable,
  Optional,
  ServiceIncidentType,
  TenderMethod,
  UUID,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useMap } from '@/composables';
import {
  CarrierPortalAction,
  CarrierShipmentSummary,
  isShipmentMilestoneAssociatedEventType,
  MessageSpecification,
  ShipmentEvent,
  ShipmentEventSource,
  ShipmentEventType,
  ShipmentStop,
  TenderAcceptRequest,
  TenderRejectRequest,
  TenderResponseCode,
  UpdateExpectedArrivalRequest,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { buildShipmentEvent } from '@/util';

import CarrierPortalAccessorialSection from '@/components/carrierPortal/CarrierPortalAccessorialSection.vue';
import CarrierPortalChargesSection from '@/components/carrierPortal/CarrierPortalChargesSection.vue';
import CarrierPortalContactSection from '@/components/carrierPortal/CarrierPortalContactSection.vue';
import CarrierPortalDriverContactSection from '@/components/carrierPortal/CarrierPortalDriverContactSection.vue';
import CarrierPortalHighlightSection from '@/components/carrierPortal/CarrierPortalHighlightSection.vue';
import CarrierPortalLineItemSection from '@/components/carrierPortal/CarrierPortalLineItemSection.vue';
import CarrierPortalLocationSection from '@/components/carrierPortal/CarrierPortalLocationSection.vue';
import CarrierPortalNotesSection from '@/components/carrierPortal/CarrierPortalNotesSection.vue';
import CarrierPortalShipmentActionButton from '@/components/carrierPortal/CarrierPortalShipmentActionButton.vue';
import CarrierPortalUpdateExpectedArrivalModal from '@/components/carrierPortal/CarrierPortalUpdateExpectedArrivalModal.vue';
import CarrierPortalDocumentSection from '@/components/carrierPortal/document/CarrierPortalDocumentSection.vue';
import CarrierPortalTimelineSection from '@/components/carrierPortal/event/CarrierPortalTimelineSection.vue';
import BillToSection from '@/components/location/BillToSection.vue';
import ReferenceNumbersSection from '@/components/referenceNumber/ReferenceNumbersSection.vue';
import ShipmentMap from '@/components/shipment/ShipmentMap.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';

const props = defineProps({
  shipmentId: {
    type: Number,
    required: true,
  },
  tenderResponse: {
    type: String as PropType<'accept' | 'reject'>,
    default: () => '',
  },
  checkCallAction: {
    type: String as PropType<'unsubscribe' | ''>,
    default: () => '',
  },
  accessToken: {
    type: String as PropType<UUID>,
    required: true,
  },
  sentFromRequiredDocumentsEmail: {
    type: Boolean,
    default: false,
  },
});
const {
  shipmentId,
  tenderResponse,
  checkCallAction,
  accessToken,
  sentFromRequiredDocumentsEmail,
} = toRefs(props);

const documentSectionRef = useTemplateRef('documentSectionRef');
const timelineSectionRef = useTemplateRef('timelineSectionRef');

const confirm = useConfirm();
const { isAuthenticatedUser, loadDomainForUnauthenticatedUser } =
  useCurrentUserStore();
const { t } = useI18n();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const router = useRouter();
const { scrollIntoView } = useScrollAnimations();
const toast = useToast();

const shipmentSummary: Ref<Optional<CarrierShipmentSummary>> = ref();
const shipment = toRef(() => shipmentSummary.value?.shipment);
const {
  isMapActionsEnabled,
  isMapExpanded,
  mapId,
  showDynamicMap,
  staticMapUrl,
} = useMap(shipment);

const loading = ref(false);
const isProcessingResponse = ref(false);
const isPrinting = ref(false);

const serviceIncidentTypes: Ref<Loadable<ServiceIncidentType[]>> = ref({
  loading: false,
});

const carrierSetting: Ref<Loadable<CarrierSetting>> = ref({ loading: false });

const updateExpectedArrivalModal = ref({
  visible: false,
  relatedStop: shipment.value?.stops[0] ?? new ShipmentStop(),
  loading: false,
});

const shipmentStops = toRef(() => shipment.value?.stops ?? []);
const acceptedRateQuote = toRef(() => shipmentSummary.value?.acceptedRateQuote);
const shipmentEvents = toRef(() => shipmentSummary.value?.shipmentEvents);
const serviceIncidents = toRef(() => shipmentSummary.value?.serviceIncidents);

const isAnyLoading = computed(() => {
  return (
    loading.value ||
    isProcessingResponse.value ||
    updateExpectedArrivalModal.value.loading
  );
});

const isInactive = computed(
  () => shipmentSummary.value?.tenderResponseMessage === 'SHIPMENT_INACTIVE',
);

const isAccepted = computed(() => {
  return (
    shipmentSummary.value?.tenderRequest?.tenderResponse?.responseCode ===
    TenderResponseCode.RESERVATION_ACCEPTED
  );
});

const isRejected = computed(() => {
  return (
    shipmentSummary.value?.tenderRequest?.tenderResponse?.responseCode ===
    TenderResponseCode.RESERVATION_DECLINED
  );
});

const isExpired = computed(() => {
  return (
    shipmentSummary.value?.tenderResponseMessage === 'TENDER_EXPIRED' ||
    shipmentSummary.value?.tenderRequest?.isExpired
  );
});

const contact = computed(() => {
  const communicationContact =
    shipmentSummary.value?.communicationContacts?.[0];
  return communicationContact?.contact;
});

const inactiveMessage = computed(() => {
  if (!contact.value?.email || !contact.value?.phone) {
    return t('carrierPortal.loadTenderResponse.inactive');
  }

  return t('carrierPortal.loadTenderResponse.inactiveParams', {
    phone: contact.value.phone,
    email: contact.value.email,
  });
});

const expiredMessage = computed(() => {
  if (!contact.value?.email || !contact.value?.phone) {
    return t('carrierPortal.loadTenderResponse.expired');
  }

  return t('carrierPortal.loadTenderResponse.expiredParams', {
    phone: contact.value.phone,
    email: contact.value.email,
  });
});

const defaultCharges = computed(() => [
  {
    description: 'Linehaul',
    code: 'LINEHAUL',
    charge: acceptedRateQuote.value?.lineHaulNetCharge ?? -1,
  },
  {
    description: 'Fuel',
    code: 'FUEL',
    charge: acceptedRateQuote.value?.fuelSurchargeAmount ?? -1,
  },
]);

const accessorialCharges = computed(
  () =>
    acceptedRateQuote.value?.accessorials?.map((a) => {
      return {
        description: a.accessorialDescription ?? 'Unknown Charge',
        code: a.code?.naturalId ?? 'UNKNOWN',
        charge: a.charge ?? -1,
      };
    }) ?? [],
);

const charges = computed(() => {
  return defaultCharges.value.concat(accessorialCharges.value);
});

const allReferenceNumbers = computed(() => {
  const shipmentReferenceNumbers = shipment.value?.referenceNumbers ?? [];
  const shipmentLineItemReferenceNumbers =
    shipment.value?.lineItems.map((li) => li.referenceNumbers).flat() ?? [];
  return [...shipmentReferenceNumbers, ...shipmentLineItemReferenceNumbers];
});

const messages = computed((): MessageSpecification[] => {
  return [
    {
      id: 'success',
      text: t('carrierPortal.loadTenderResponse.acceptedShipmentId', {
        shipmentId: shipmentId.value,
      }),
      severity: 'success',
      visible: isAccepted.value,
    },
    {
      id: 'expired-warning',
      text: expiredMessage.value,
      severity: 'warn',
      visible: isExpired.value,
    },
    {
      id: 'rejected-warning',
      text: t('carrierPortal.loadTenderResponse.rejectedShipmentId', {
        shipmentId: shipmentId.value,
      }),
      severity: 'warn',
      visible: isRejected.value,
    },
    {
      id: 'after-booked-warning',
      text: t('carrierPortal.loadTenderResponse.afterBooked'),
      severity: 'warn',
      visible:
        shipmentSummary.value?.tenderResponseMessage ===
        'CANNOT_REJECT_AFTER_BOOKED',
    },
    {
      id: 'inactive-error',
      text: inactiveMessage.value,
      severity: 'error',
      visible: isInactive.value,
    },
    {
      id: 'generic-error',
      text: t('carrierPortal.loadTenderResponse.problem'),
      severity: 'error',
      visible:
        !!tenderResponse.value &&
        !shipmentSummary.value?.tenderRequest?.tenderResponse?.responseCode,
    },
  ];
});

const loadCarrierSetting = async () => {
  const scac = shipmentSummary.value?.carrier?.scac;
  if (!scac || !accessToken.value) {
    return;
  }

  carrierSetting.value = { loading: true };
  try {
    const data = await api.carrierPortal.findCarrierSettingsForScacAndShipment(
      scac,
      shipmentId.value,
      accessToken.value,
    );
    carrierSetting.value = { loading: false, data };
  } catch (e) {
    if (axios.isCancel(e)) {
      carrierSetting.value = { loading: false };
      return;
    }

    console.error('Error loading carrier settings', e);
    carrierSetting.value = { loading: false, error: e };
  }
};

const loadServiceIncidentTypes = async () => {
  if (!shipmentId.value || !accessToken.value) {
    return;
  }

  try {
    serviceIncidentTypes.value.loading = true;
    serviceIncidentTypes.value.data =
      await api.carrierPortal.loadServiceIncidentTypes(
        shipmentId.value,
        accessToken.value,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading service incident types', e);
    serviceIncidentTypes.value.error = e;
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.paramTypes', {
          param: t('events.incidents'),
          count: 2,
        }),
      }),
      error: e,
    });
  } finally {
    serviceIncidentTypes.value.loading = false;
  }
};

const processResponse = async (
  tenderResponse?: 'accept' | 'reject',
  shipmentSummary?: Optional<CarrierShipmentSummary>,
) => {
  if (
    !tenderResponse ||
    !shipmentSummary?.shipment?.id ||
    isInactive.value ||
    isExpired.value
  ) {
    return;
  }

  const tenderResponseCode =
    shipmentSummary?.tenderRequest?.tenderResponse?.responseCode;
  if (!tenderResponseCode) {
    if (tenderResponse === 'accept') {
      await processAcceptResponse();
    } else if (tenderResponse === 'reject') {
      await processRejectResponse();
    }
  } else if (isAccepted.value && tenderResponse === 'reject') {
    await onRejectClicked();
  } else if (isRejected.value && tenderResponse === 'accept') {
    await onAcceptRejectedTender();
  } else if (tenderResponse) {
    console.error(`unexpected load tender response "${tenderResponse}"`);
  }

  // Get the tender response command out of the url
  const query = { ...router.currentRoute.value.query };
  delete query.tenderResponse;

  await router.replace({ query });
};

const loadShipmentSummary = async () => {
  try {
    loading.value = true;
    shipmentSummary.value = await api.carrierPortal.getShipmentSummary(
      shipmentId.value,
      {
        accessToken: accessToken.value,
        withTenderRequest: true,
      },
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading shipment', e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('shipments.shipments'),
        id: shipmentId.value,
      }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

const processAcceptResponse = async () => {
  try {
    isProcessingResponse.value = true;
    shipmentSummary.value = await api.carrierPortal.acceptTender(
      shipmentId.value,
      new TenderAcceptRequest(TenderMethod.EMAIL),
      accessToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('tenderAccept error', shipmentId.value, e);
    toast.addError({
      detail: t('shipments.errors.tenderConfirm'),
      error: e,
    });
    throw e;
  } finally {
    isProcessingResponse.value = false;
  }
};

const processRejectResponse = async () => {
  try {
    isProcessingResponse.value = true;
    shipmentSummary.value = await api.carrierPortal.rejectTender(
      shipmentId.value,
      new TenderRejectRequest(TenderMethod.EMAIL),
      accessToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('tenderReject error', shipmentId.value, e);
    toast.addError({
      detail: t('shipments.errors.tenderReject'),
      error: e,
    });
    throw e;
  } finally {
    isProcessingResponse.value = false;
  }
};

const onRejectClicked = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('shipments.rejectTender'),
    message: t('shipments.rejectTenderConfirmation'),
    accept: processRejectResponse,
    acceptClass: 'p-button-sm p-button-danger',
    acceptLabel: t('common.reject'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.cancelAndClose'),
  });
};

const onAcceptRejectedTender = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('common.acceptParam', { param: t('shipments.tender') }),
    message: t('shipments.acceptTenderConfirmation'),
    accept: processAcceptResponse,
    acceptClass: 'p-button-sm p-button-danger',
    acceptLabel: t('common.acceptParam'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.cancelAndClose'),
  });
};

const buildCarrierPortalEvent = (event: Partial<ShipmentEvent>) => {
  if (!isShipmentMilestoneAssociatedEventType(event.eventType)) {
    return buildShipmentEvent({
      shipmentId: shipment.value?.id,
      carrierId: shipment.value?.carrierId,
      actualDate: DateTime.now(),
      source: ShipmentEventSource.CARRIER_PORTAL,
      ...event,
    });
  }

  const existingEvent = shipmentEvents.value?.find((e) => {
    return (
      e.eventType === event.eventType &&
      e.relatedStopId === event.relatedStop?.id
    );
  });

  if (!existingEvent) {
    throw new Error(`Couldn't find existing ${event.eventType} event`);
  }

  existingEvent.actualDate = DateTime.now();
  return existingEvent;
};

const onCommand = (command: ActionCommand<CarrierPortalAction>) => {
  const { action } = command;

  const timelineSection = timelineSectionRef.value as
    typeof CarrierPortalTimelineSection | null;

  if (action === CarrierPortalAction.ATTACH_DOCUMENT) {
    const docSection = documentSectionRef.value as
      typeof CarrierPortalDocumentSection | null;
    docSection?.showModal();
  } else if (action === CarrierPortalAction.TENDER_CONFIRM) {
    if (isRejected.value) {
      onAcceptRejectedTender();
    } else {
      processAcceptResponse();
    }
  } else if (action === CarrierPortalAction.TENDER_REJECT) {
    if (isAccepted.value) {
      onRejectClicked();
    } else {
      processRejectResponse();
    }
  } else if (action === CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL) {
    updateExpectedArrivalModal.value.relatedStop = command.relatedStop;
    updateExpectedArrivalModal.value.visible = true;
  } else if (action === CarrierPortalAction.INCIDENT) {
    timelineSection?.onAddServiceIncident();
  } else {
    // The remaining action types are all event types
    const eventTypeKey: keyof typeof ShipmentEventType =
      CarrierPortalAction[action];
    const eventType = ShipmentEventType[eventTypeKey];

    const event = buildCarrierPortalEvent({
      eventType,
      relatedStop: command.relatedStop,
      actualDate: command.actualDate,
      severity: command.severity,
    });
    timelineSection?.onAddEvent(event);
  }
};

const onUpdateExpectedArrival = async (
  updateRequest: UpdateExpectedArrivalRequest,
) => {
  try {
    updateExpectedArrivalModal.value.loading = true;
    isAuthenticatedUser.value
      ? await api.shipment.updateExpectedArrival(
          shipmentId.value,
          updateRequest,
        )
      : await api.carrierPortal.updateExpectedArrival(
          shipmentId.value,
          updateRequest,
          accessToken.value,
        );
    await loadShipmentSummary();
    toast.addSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('shipments.dates.expectedArrival'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error updating expected arrival', e);
    toast.addError({
      detail: t('errors.updatingParam', {
        param: t('shipments.dates.expectedArrival'),
      }),
      error: e,
    });
  } finally {
    updateExpectedArrivalModal.value.loading = false;
  }
};

const processCheckCallAction = async () => {
  if (checkCallAction.value === 'unsubscribe') {
    await cancelCheckCallEmails(shipmentId.value);
  }
};

const cancelCheckCallEmails = async (shipmentId: number) => {
  try {
    isAuthenticatedUser.value
      ? await api.shipmentCheckCallReminder.cancelCheckCallReminders(shipmentId)
      : await api.carrierPortal.cancelCheckCallReminders(
          shipmentId,
          accessToken.value,
        );
    toast.addSuccess({
      detail: t('messages.successCancelingParam', {
        param: t('carrierPortal.checkCallReminders.checkCallReminders', 2),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('carrierPortal.checkCallReminders.checkCallReminders', 2),
      }),
      error: e,
    });
  }
};

const loadShipment = async () => {
  await loadShipmentSummary();
  try {
    await processResponse(tenderResponse.value, shipmentSummary.value);
  } finally {
    loadCarrierSetting();
  }
};

watch(
  shipmentId,
  () => {
    loadShipment();
    processCheckCallAction();
    loadServiceIncidentTypes();
  },
  { immediate: true },
);

watch(documentSectionRef, (section) => {
  if (!section || !sentFromRequiredDocumentsEmail.value) {
    return;
  }
  scrollIntoView(section.$el);
});

watch(
  () => shipmentSummary.value?.shipment?.owner?.id,
  (ownerId) => {
    if (ownerId) {
      loadDomainForUnauthenticatedUser(ownerId, accessToken.value);
    }
  },
);

onMounted(async () => {
  setParentEntity(shipment);
});

onUnmounted(() => {
  clearParentEntity();
});

defineExpose({
  $primevue: usePrimeVue(),
});
provide('shipmentStops', shipmentStops);
</script>

<style>
#carrier-portal {
  .content-header {
    &.dark-bg {
      .carrier-portal-actions {
        .separator {
          color: white;
        }
      }
    }

    .content-header-left {
      flex: 1 0 auto;

      .content-heading-container {
        flex: 1 0 auto;
      }
    }
  }
}
</style>
